/**
 * Panel R_eff breakdown — expected vs actual on one line per factor.
 *
 * R_eff = R0 · (actual_disp / expected_disp) · [actual_vig / expected_vig when 2w]
 */
import React from 'react';

function fmt(v, digits = 4) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toFixed(digits);
}

function fmtMult(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (n >= 10) return `×${n.toFixed(1)}`;
  return `×${n.toFixed(2)}`;
}

function ratioTone(ratio) {
  if (ratio == null || !Number.isFinite(Number(ratio))) return 'muted';
  const r = Number(ratio);
  if (r > 1.25) return 'hot'; // inflate
  if (r < 0.8) return 'cool'; // compress
  return 'ok';
}

/** Compact bar: expected at center (1.0); actual marker scales log-ish for readability. */
function RatioBar({ ratio }) {
  if (ratio == null || !Number.isFinite(Number(ratio)) || Number(ratio) <= 0) {
    return <div className="unc-ratio-bar unc-ratio-bar--empty" aria-hidden />;
  }
  const r = Number(ratio);
  // Map ratio to 5%–95% of track; 1.0 sits at 50%.
  const logPos = 0.5 + 0.45 * (Math.log(r) / Math.log(4)); // ×4 → ~95%, ÷4 → ~5%
  const pct = Math.max(5, Math.min(95, logPos * 100));
  return (
    <div className="unc-ratio-bar" title={`multiplier ${fmtMult(r)}`}>
      <div className="unc-ratio-track">
        <span className="unc-ratio-mid" />
        <span className="unc-ratio-marker" style={{ left: `${pct}%` }} />
      </div>
    </div>
  );
}

function CompareRow({
  name,
  hint,
  expected,
  actual,
  ratio,
  expectedLabel = 'expected',
  actualLabel = 'actual',
  omitted = false,
}) {
  const tone = omitted ? 'muted' : ratioTone(ratio);
  return (
    <div className={`unc-compare${omitted ? ' unc-compare--omitted' : ''}`}>
      <div className="unc-compare-head">
        <span className="unc-compare-name" title={hint}>
          {name}
        </span>
        <span className={`unc-compare-mult unc-compare-mult--${tone}`}>
          {omitted ? 'omitted' : fmtMult(ratio)}
        </span>
      </div>
      <div className="unc-compare-line">
        <div className="unc-compare-cell">
          <span className="unc-compare-k">{expectedLabel}</span>
          <span className="unc-compare-v">{expected}</span>
        </div>
        <span className="unc-compare-vs" aria-hidden>
          →
        </span>
        <div className="unc-compare-cell">
          <span className="unc-compare-k">{actualLabel}</span>
          <span className="unc-compare-v">{actual}</span>
        </div>
      </div>
      {!omitted ? <RatioBar ratio={ratio} /> : null}
    </div>
  );
}

export default function UncertaintyBreakdownPanel({ quote }) {
  if (!quote) {
    return (
      <div className="card unc-bd-card">
        <h3>Uncertainty</h3>
        <div className="empty-state compact">Select a market</div>
      </div>
    );
  }

  const r0 = quote.R0;
  const ed = quote.expected_disp;
  const ev = quote.expected_vig;
  const vDisp = quote.V_disp;
  const sPanel = quote.s_panel != null ? quote.s_panel : quote.vig_implied;
  const dispRatio =
    quote.disp_ratio != null
      ? Number(quote.disp_ratio)
      : ed != null && vDisp != null && Number(ed) > 0
        ? Number(vDisp) / Number(ed)
        : null;
  const vigRatio = quote.vig_ratio != null ? Number(quote.vig_ratio) : null;
  const applyVig = quote.apply_vig;
  const vigOmitted = applyVig === false;
  const rEff = quote.R_eff;
  const P = quote.P;
  const hasBreakdown =
    rEff != null || vDisp != null || r0 != null || (ed != null && ev != null);

  const product =
    r0 != null &&
    dispRatio != null &&
    Number.isFinite(Number(r0)) &&
    Number.isFinite(dispRatio)
      ? Number(r0) *
        dispRatio *
        (vigOmitted || vigRatio == null ? 1 : Number(vigRatio))
      : null;

  return (
    <div className="card unc-bd-card">
      <h3>Uncertainty sourcing</h3>
      <p className="unc-bd-lede">
        Each row: <strong>expected</strong> (config) vs <strong>actual</strong> (live panel). Multiplier
        &gt;1 widens R_eff; &lt;1 tightens it.
        {quote.quote_scenario_label || quote.quote_scenario
          ? ` · ${quote.quote_scenario_label || quote.quote_scenario}`
          : ''}
        {quote.scenario_n_two_sided != null
          ? ` · ${quote.scenario_n_two_sided} two-sided / ${quote.scenario_n_one_sided ?? 0} one-sided`
          : ''}
      </p>

      {!hasBreakdown ? (
        <div className="empty-state compact">
          No panel breakdown yet for this market (waiting on kf_diagnostics / uncertainty_params).
        </div>
      ) : (
        <>
          <CompareRow
            name="Book disagreement"
            hint="V_disp / expected_disp (logit²)"
            expected={fmt(ed, 4)}
            actual={fmt(vDisp, 4)}
            ratio={dispRatio}
            expectedLabel="expected_disp"
            actualLabel="V_disp"
          />

          <CompareRow
            name="Panel vig (hold)"
            hint="S_panel / expected_vig — omitted when panel is one-sided"
            expected={fmt(ev, 4)}
            actual={vigOmitted ? '—' : fmt(sPanel, 4)}
            ratio={vigOmitted ? null : vigRatio}
            expectedLabel="expected_vig"
            actualLabel="S_panel"
            omitted={vigOmitted}
          />

          {quote.missing_vig_imputed ? (
            <p className="unc-bd-note">
              Missing expected books padded into vig at{' '}
              {quote.missing_vig_value != null
                ? `${(Number(quote.missing_vig_value) * 100).toFixed(1)}%`
                : 'margin_ceiling'}
              .
            </p>
          ) : null}

          <div className="unc-result">
            <div className="unc-result-formula">
              <span className="unc-result-k">R_eff</span>
              <span className="unc-result-eq">
                = {fmt(r0, 4)}
                <span className="unc-result-op">×</span>
                {fmtMult(dispRatio).replace('×', '')}
                {!vigOmitted ? (
                  <>
                    <span className="unc-result-op">×</span>
                    {fmtMult(vigRatio).replace('×', '')}
                  </>
                ) : (
                  <span className="unc-result-skip"> × vig omitted</span>
                )}
                <span className="unc-result-op">=</span>
                <strong>{fmt(rEff != null ? rEff : product, 5)}</strong>
              </span>
            </div>
            <div className="unc-result-meta">
              <span title="Config base when both ratios ≈ 1">
                R0 <strong>{fmt(r0, 4)}</strong>
              </span>
              <span title="Published posterior (may include dropout scar above panel R_eff)">
                Quote P <strong>{fmt(P, 5)}</strong>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
